import React, { useState } from "react";
import SFTypography from "../../../typography";
import { SFStack } from "../../../grid";
import NavButton from "../navButton";
import { getPageTitleStatusTemplate } from "../../helpers/getPageTitleStatusTemplates";
import getStyled from "../../../../hooks/getStyled";
import PropTypes from "prop-types";
import { PAGE_TITLE_STATUS } from "../../../../constants/actionBar";
import SFTooltip from "../../../tooltip";
import { SFInfoIcon } from "../../../icons";

const { styled } = getStyled();

const StatusWrapper = styled(SFStack)(() => ({
  "& .MuiSvgIcon-root": {
    width: "14px",
    height: "14px",
  },
}));

const InfoIconWrapper = styled("div")(({ theme }) => ({
  height: "35px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
}));

const TitleWrapper = styled(SFStack)(({ theme }) => ({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  flexGrow: 1,
  minWidth: 0, // Ensures the title can shrink properly
}));

const SFStyledStack = styled(SFStack)(({ theme }) => ({
  overflow: "hidden",
}));

const PageTitleRenderer = ({
  title,
  code,
  status,
  tooltip,
  navDefs,
  navMenuAnchorEl,
}) => {
  const [navButtonSelected, setNavButtonSelected] = useState(false);

  const toggleNavButton = () => {
    setNavButtonSelected((prev) => !prev);
  };

  const statusTemplate = getPageTitleStatusTemplate(status);

  return (
    <SFStyledStack direction="row" alignItems="center" spacing={2}>
      {navDefs.length > 0 && (
        <NavButton
          selected={navButtonSelected}
          onClick={toggleNavButton}
          menuAnchorEl={navMenuAnchorEl}
          navDefs={navDefs}
        />
      )}
      <TitleWrapper direction="row" alignItems="flex-end" spacing={0.5}>
        {typeof title === "string" ? (
          <SFTypography noWrap variant="cardSectionHeading" component="h2">
            {title}
          </SFTypography>
        ) : (
          title
        )}

        {code && <SFTypography variant="textValue">{`(${code})`}</SFTypography>}
      </TitleWrapper>
      {tooltip && (
        <SFTooltip placement="right" width="wide" content={tooltip}>
          <InfoIconWrapper alignItems="center" justifyContent="center">
            <SFInfoIcon size="small" color="grey400" />
          </InfoIconWrapper>
        </SFTooltip>
      )}
      {status && statusTemplate && (
        <StatusWrapper direction="row" alignItems="center" spacing={0.5}>
          {statusTemplate.icon}
          <SFTypography variant="textValue">
            {statusTemplate.label}
          </SFTypography>
        </StatusWrapper>
      )}
    </SFStyledStack>
  );
};

PageTitleRenderer.propTypes = {
  // The page title
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  // A reference value associated with the title (displayed in parentheses)
  code: PropTypes.string,
  // The tooltip to display when hovering over a rendered tooltip icon
  tooltip: PropTypes.string,
  // A status value associated with the title (displayed as an icon and label)
  status: PropTypes.oneOf(Object.values(PAGE_TITLE_STATUS)),
  // The navigation definitions to render as a third level navigation menu
  navDefs: PropTypes.arrayOf(
    PropTypes.shape({
      // The icon to display in the button
      icon: PropTypes.element,
      // The header text to display in the button
      header: PropTypes.string,
      // The description text to display in the button
      description: PropTypes.string,
      // The path to navigate to when the button is clicked
      path: PropTypes.string,
      // Whether the button is the default (renders a check icon)
      default: PropTypes.bool,
    })
  ),
  // The element to anchor the third level navigation menu to
  navMenuAnchorEl: PropTypes.object,
};

PageTitleRenderer.defaultProps = {
  title: "Page Title",
  reference: "",
  tooltip: "",
  navDefs: [],
};

export default PageTitleRenderer;
